/**
 * lib/render-policy/spec-key-map.ts — canonical display labels for appliance
 * spec keys (PIP lean rebuild, WS4).
 *
 * getDisplaySpecs (display-specs.ts) unions spec KEYS across sources, each of
 * which uses its own key casing (snake_case from feed metadata, camelCase from
 * scrapes, Title Case from manufacturer tables). This map normalizes the
 * ~15 obvious cooling-appliance keys to human labels; anything unmapped falls
 * through to `snakeOrCamelToTitleCase` so no key is ever dropped.
 *
 * Pure, dependency-free — imported by resolvers and unit-tested directly.
 */

/**
 * Curated key → display label. Lookup is done on the NORMALIZED key
 * (lower-cased, non-alphanumeric collapsed to `_`) so `capacity_cuft`,
 * `capacityCuFt`, and `Capacity Cu Ft` all resolve to the same entry.
 */
export const SPEC_KEY_LABELS: Record<string, string> = {
  capacity_cuft: 'Capacity (cu ft)',
  capacity_cu_ft: 'Capacity (cu ft)',
  total_capacity_cuft: 'Total Capacity (cu ft)',
  width_in: 'Width',
  height_in: 'Height',
  depth_in: 'Depth',
  weight_lbs: 'Weight',
  energy_star: 'ENERGY STAR',
  annual_energy_kwh: 'Annual Energy Use (kWh)',
  energy_kwh_per_year: 'Annual Energy Use (kWh)',
  energy_kwh_yr: 'Annual Energy Use (kWh)',
  color: 'Color',
  finish: 'Finish',
  door_style: 'Door Style',
  ice_maker: 'Ice Maker',
  water_dispenser: 'Water Dispenser',
  number_of_doors: 'Number of Doors',
  defrost_type: 'Defrost Type',
  installation_type: 'Installation Type',
  // FRIDGE-280: manufacturer-PDF-mined phrase ("Built-in or freestanding") on
  // canonical_products.manufacturer_specs — 249 FINs live 08/29/2026. Must
  // share the legacy explicit row's EXACT label so dedupeSpecsByLabel
  // collapses the two into one row (legacy specs_json.installation wins).
  install_requirements: 'Installation Type',
  warranty_years: 'Warranty (years)',
  noise_level_db: 'Noise Level (dB)',
};

/**
 * Collapse a raw spec key to its normalized lookup form: lower-cased, with any
 * run of non-alphanumerics (spaces, camelCase boundaries, punctuation) turned
 * into a single underscore. `capacityCuFt` → `capacity_cu_ft`,
 * `Capacity (cu ft)` → `capacity_cu_ft`.
 */
export function normalizeSpecKey(key: string): string {
  if (!key) return '';
  return key
    // insert boundary between camelCase / letter-digit transitions
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Za-z])([0-9])/g, '$1_$2')
    .replace(/([0-9])([A-Za-z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Fallback: turn a snake_case / camelCase key into Title Case words when it is
 * not in the curated map. `door_hinge_side` → `Door Hinge Side`;
 * `numberOfDoors` → `Number Of Doors`.
 */
export function snakeOrCamelToTitleCase(key: string): string {
  const norm = normalizeSpecKey(key);
  if (!norm) return key;
  return norm
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Resolve the human display label for a spec key: curated map first (via the
 * normalized form), Title-Case fallback otherwise.
 */
export function displayLabelForSpecKey(key: string): string {
  const norm = normalizeSpecKey(key);
  return SPEC_KEY_LABELS[norm] ?? snakeOrCamelToTitleCase(key);
}
